import clsx from 'clsx';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { type InlineMentionData, selectInline } from '@gitbook/doc-core';
import type { InlineProps } from '@gitbook/slate-react';
import { type IconDefinition, renderIcon } from '@gitbook/spine-react';

import {
    type UIResolvedContentReference,
    useContentReference,
    useContentRefNavigationProps,
} from '../../ContentReferences';
import { useIsHiddenOnDiffSide } from '../../Diff';
import {
    InlineContentRefEditPopover,
    InlineContentRefPreviewPopover,
    useIsInlineActive,
} from '../inlines';
import { ALLOWED_CONTENT_REFS_IN_INLINE_MENTION } from './util';

/**
 * Component to render an inline mention.
 */
export function InlineMention(props: InlineProps): React.ReactElement {
    const isHiddenOnDiffSide = useIsHiddenOnDiffSide(props.node);
    if (isHiddenOnDiffSide) {
        return (
            <span {...props.attributes} hidden>
                {props.children}
            </span>
        );
    }

    return (
        <React.Suspense fallback={<MentionSpan label="Loading…" {...props.attributes} aria-busy />}>
            <ResolvedInlineMention {...props} />
        </React.Suspense>
    );
}

/**
 * Resolves an inline mention, it suspenses.
 */
function ResolvedInlineMention(props: InlineProps): React.ReactElement {
    const { node, attributes, editor, isSelected } = props;

    const data = node.data.toFrozenObject() as InlineMentionData;
    const resolved = useContentReference(data.ref);
    const [isActive, inlineProps] = useIsInlineActive(props);

    const isEditable = !editor.readOnly;

    const mention = (
        <ContentRefMention
            readOnly={editor.readOnly}
            resolved={resolved}
            contentRef={data.ref}
            onClick={
                isEditable
                    ? () => {
                          editor.change((change) => {
                              selectInline(change, node);
                          });
                      }
                    : undefined
            }
            isActive={isSelected}
            {...attributes}
            {...inlineProps}
        />
    );

    if (!isActive) {
        return mention;
    }

    if (isEditable) {
        return (
            <InlineContentRefEditPopover
                label="Mention"
                resolved={resolved}
                contentRef={data.ref}
                accept={ALLOWED_CONTENT_REFS_IN_INLINE_MENTION}
                {...props}
            >
                {mention}
            </InlineContentRefEditPopover>
        );
    }

    return (
        <InlineContentRefPreviewPopover label="Mention" resolved={resolved} contentRef={data.ref}>
            {mention}
        </InlineContentRefPreviewPopover>
    );
}

/**
 * Render a mention with a resolved content ref.
 */
const ContentRefMention = function ContentRefMention({
    ref,
    ...props
}: (React.HTMLAttributes<HTMLElement> &
    Pick<ContentRefLinkMentionProps, 'contentRef' | 'readOnly' | 'isActive'> &
    Pick<MentionSpanProps, 'isActive'> & {
        resolved: UIResolvedContentReference;
    }) & { ref?: React.ForwardedRef<HTMLElement> }) {
    const { contentRef, resolved, readOnly, ...rest } = props;
    switch (contentRef.kind) {
        case 'user': {
            return <MentionSpan ref={ref} {...transformMention(contentRef, resolved)} {...rest} />;
        }
        default: {
            return (
                <ContentRefLinkMention
                    ref={ref as React.ForwardedRef<HTMLAnchorElement>}
                    {...props}
                />
            );
        }
    }
};

type ContentRefLinkMentionProps = Omit<MentionAnchorProps, 'label' | 'icon'> & {
    contentRef: api.ContentRef;
    readOnly: boolean;
    resolved: UIResolvedContentReference;
};

/**
 * Render a mention that is a link.
 */
const ContentRefLinkMention = function ContentRefLinkMention({
    ref,
    ...props
}: ContentRefLinkMentionProps & { ref?: React.ForwardedRef<HTMLAnchorElement> }) {
    const { contentRef, resolved, readOnly, ...rest } = props;
    const navProps = useContentRefNavigationProps(readOnly ? resolved : null, props);
    return (
        <MentionAnchor
            ref={ref}
            {...rest}
            {...navProps}
            {...transformMention(contentRef, resolved)}
        />
    );
};

type MentionProps = {
    className?: string;
    icon?: IconDefinition;
    label: string;
    isActive?: boolean;
};

/**
 * Common hook to render low-level mention tag.
 */
function useMentionProps<T extends object>(props: MentionProps & T) {
    const { className, icon, label, isActive, ...rest } = props;
    return {
        className: clsx(
            'rounded-sm bg-info-ui px-1 pt-[1px] pb-[0.5px] whitespace-nowrap text-link-low no-underline select-none hover:bg-info-ui-hover hover:text-link-high',
            'href' in rest && rest.href ? 'cursor-pointer' : 'cursor-default',
            isActive ? 'bg-active' : 'bg-[var(--sp-color-bg-mention-default)]',
            className
        ),
        'data-mention': '',
        children: (
            <>
                {icon ? (
                    <span className="relative -top-[1px] mr-[0.25em] inline-flex align-middle">
                        {renderIcon(icon)}
                    </span>
                ) : null}
                <span className="whitespace-pre-wrap">{label}</span>
            </>
        ),
        ...rest,
    };
}

type MentionSpanProps = React.ComponentPropsWithoutRef<'span'> & MentionProps;

/**
 * Low-level mention span.
 */
const MentionSpan = function MentionSpan({
    ref,
    ...props
}: MentionSpanProps & { ref?: React.ForwardedRef<HTMLSpanElement> }) {
    const renderProps = useMentionProps(props);
    return <span ref={ref} {...renderProps} />;
};

type MentionAnchorProps = React.ComponentPropsWithoutRef<'a'> & MentionProps;

/**
 * Low-level mention anchor.
 */
const MentionAnchor = function MentionAnchor({
    ref,
    ...props
}: MentionAnchorProps & { ref?: React.ForwardedRef<HTMLAnchorElement> }) {
    const renderProps = useMentionProps(props);
    return <a ref={ref} {...renderProps} />;
};

/**
 * Transform the resolution of content ref into what should be displayed in the mention.
 */
function transformMention(
    contentRef: api.ContentRef,
    resolved: UIResolvedContentReference
): MentionProps {
    switch (contentRef.kind) {
        case 'user': {
            // We display agent mentions as "@GitBook" to make it easier
            // and align with how the experience is in Slack, etc.
            if (contentRef.user === api.GitBookUsers.GitbookAgent) {
                return {
                    icon: null,
                    label: '@GitBook',
                };
            }

            return {
                icon: resolved.icon || resolved.secondaryIcon,
                label: `@${resolved.title}`,
            };
        }
        case 'space':
        case 'page': {
            return {
                icon: resolved.icon || resolved.secondaryIcon,
                label: resolved.title,
            };
        }
        default: {
            return {
                icon: resolved.secondaryIcon,
                label: resolved.title,
            };
        }
    }
}
